import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  DirectionsCar as CarIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  VehicleRental,
  VEHICLE_RENTAL_BILLING_LABELS,
  VEHICLE_RENTAL_STATUS_LABELS,
  PAYMENT_METHODS,
  PAYMENT_STATUS,
} from '../../types';
import vehicleRentalService from '../../services/vehicleRentalService';
import { useAuthUser } from '../../contexts/AuthUserContext';

const formatCurrency = (n: number | null | undefined) =>
  n != null && Number.isFinite(Number(n))
    ? Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatDate = (d?: string | null) => {
  if (!d) return '—';
  try {
    return format(parseISO(d.length > 10 ? d : `${d}T12:00:00`), "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return d;
  }
};

const RentalDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canWrite } = useAuthUser();
  const [rental, setRental] = useState<VehicleRental | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await vehicleRentalService.getById(Number(id));
        if (!cancelled) setRental(data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError('Não foi possível carregar esta locação.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const patchStatus = async (status: VehicleRental['status']) => {
    if (!id || !rental) return;
    setActionError(null);
    setStatusLoading(true);
    try {
      const updated = await vehicleRentalService.updateStatus(Number(id), status);
      setRental(updated);
    } catch (e) {
      console.error(e);
      setActionError('Não foi possível atualizar o status. O endpoint pode ainda não existir na API.');
    } finally {
      setStatusLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error || !rental) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Locação não encontrada.'}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/locacoes')}>
          Voltar à lista
        </Button>
      </Box>
    );
  }

  const v = rental.vehicle;
  const vehicleTitle = v ? `${v.brand} ${v.model} (${v.year})` : `Veículo #${rental.vehicle_id}`;
  const amount =
    rental.billing_type === 'daily'
      ? rental.total_amount ??
        (rental.daily_rate != null && rental.number_of_days != null
          ? Number(rental.daily_rate) * Number(rental.number_of_days)
          : null)
      : rental.monthly_amount;

  const statusChipColor = (s: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch (s) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Fade in>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/locacoes')} color="inherit">
            Voltar
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
            Locação #{rental.id}
          </Typography>
          <Chip label={VEHICLE_RENTAL_STATUS_LABELS[rental.status]} color={statusChipColor(rental.status)} />
          {canWrite && (
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/locacoes/editar/${rental.id}`)}>
              Editar
            </Button>
          )}
        </Box>

        {actionError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        {canWrite && (rental.status === 'pending' || rental.status === 'active') && (
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Ações rápidas
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {rental.status === 'pending' && (
                <Button size="small" variant="outlined" disabled={statusLoading} onClick={() => void patchStatus('active')}>
                  Marcar como ativa
                </Button>
              )}
              {rental.status === 'active' && (
                <Button size="small" variant="outlined" disabled={statusLoading} onClick={() => void patchStatus('completed')}>
                  Encerrar locação
                </Button>
              )}
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={statusLoading}
                onClick={() => {
                  if (window.confirm('Cancelar esta locação?')) void patchStatus('cancelled');
                }}
              >
                Cancelar
              </Button>
            </Box>
          </Paper>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CarIcon fontSize="small" /> Veículo
                </Typography>
                <Typography variant="h6">{vehicleTitle}</Typography>
                {v?.plate && (
                  <Typography variant="body2" color="text.secondary">
                    Placa: {v.plate}
                  </Typography>
                )}
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Cobrança
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {VEHICLE_RENTAL_BILLING_LABELS[rental.billing_type]}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Período: {formatDate(rental.start_date)}
                  {rental.end_date ? ` — ${formatDate(rental.end_date)}` : ''}
                </Typography>
                {rental.billing_type === 'daily' && (
                  <>
                    {rental.daily_rate != null && (
                      <Typography variant="body2">Diária: {formatCurrency(rental.daily_rate)}</Typography>
                    )}
                    {rental.number_of_days != null && (
                      <Typography variant="body2">Quantidade de diárias: {rental.number_of_days}</Typography>
                    )}
                  </>
                )}
                {rental.billing_type === 'monthly' && rental.monthly_amount != null && (
                  <Typography variant="body2">Mensalidade: {formatCurrency(rental.monthly_amount)}</Typography>
                )}
                <Typography variant="h6" color="primary.main" sx={{ mt: 2 }}>
                  Total / referência: {formatCurrency(amount)}
                </Typography>
                {rental.next_due_date && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Próximo vencimento: {formatDate(rental.next_due_date)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon fontSize="small" /> Locatário
                </Typography>
                <Typography variant="h6">{rental.renter_name}</Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <PhoneIcon fontSize="inherit" /> {rental.renter_phone}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Doc.: {rental.renter_document}
                </Typography>
                {rental.renter_email && (
                  <Typography variant="body2" color="text.secondary">
                    {rental.renter_email}
                  </Typography>
                )}
                {(rental.renter_zip ||
                  rental.renter_address ||
                  rental.renter_number ||
                  rental.renter_complement ||
                  rental.renter_neighborhood ||
                  rental.renter_city ||
                  rental.renter_state) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Endereço
                    </Typography>
                    {(rental.renter_address || rental.renter_number || rental.renter_complement) && (
                      <Typography variant="body2" color="text.secondary">
                        {[rental.renter_address, rental.renter_number ? `nº ${rental.renter_number}` : null, rental.renter_complement]
                          .filter(Boolean)
                          .join(', ')}
                      </Typography>
                    )}
                    {rental.renter_neighborhood && (
                      <Typography variant="body2" color="text.secondary">
                        {rental.renter_neighborhood}
                      </Typography>
                    )}
                    {(rental.renter_city || rental.renter_state) && (
                      <Typography variant="body2" color="text.secondary">
                        {[rental.renter_city, rental.renter_state].filter(Boolean).join(' / ')}
                      </Typography>
                    )}
                    {rental.renter_zip && (
                      <Typography variant="body2" color="text.secondary">
                        CEP: {rental.renter_zip}
                      </Typography>
                    )}
                  </>
                )}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PaymentIcon fontSize="small" /> Pagamento
                </Typography>
                <Typography variant="body1">{PAYMENT_METHODS[rental.payment_method]}</Typography>
                <Chip size="small" label={PAYMENT_STATUS[rental.payment_status]} sx={{ mt: 1 }} variant="outlined" />
              </CardContent>
            </Card>
          </Grid>

          {rental.notes?.trim() && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Observações
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{rental.notes}</Typography>
              </Paper>
            </Grid>
          )}

          {rental.installments && rental.installments.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Cobranças agendadas
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Vencimento</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Pago em</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rental.installments.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell>{formatDate(it.due_date)}</TableCell>
                        <TableCell align="right">{formatCurrency(it.amount)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={PAYMENT_STATUS[it.status]} variant="outlined" />
                        </TableCell>
                        <TableCell>{it.paid_at ? formatDate(it.paid_at) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
      </Box>
    </Fade>
  );
};

export default RentalDetails;
